import moment from 'moment-timezone'

// XAN-193 (adjustments) — a ticket's due date is a calendar DAY, not an instant.
//
// The form posts "YYYY-MM-DD" and the API parses it as UTC midnight, so the value that comes back
// is e.g. "2026-09-11T00:00:00.000Z". Formatting that with the app-wide default zone
// (America/Bogota, see computeds.js) renders 10/09 19:00 — the day BEFORE the one the user picked —
// and comparing it against `new Date()` flags a ticket as overdue from 7 p.m. on the day before it
// is due. Everything that shows or compares a due date goes through here instead, so the whole
// module agrees on which day a ticket is due.
//
// "Today" is taken in Bogota because that is where the team works: a ticket due on the 12th is
// "due today" for the whole Colombian 12th, regardless of the browser's clock settings.
const BUSINESS_TZ = 'America/Bogota'

export const OPEN_STATUSES = Object.freeze(['BACKLOG', 'TODO', 'IN_PROGRESS'])

export const isOpenStatus = (status) => OPEN_STATUSES.includes(status)

// Calendar day the ticket is due, as "YYYY-MM-DD", or null when it has no due date.
export const dueDateKey = (dueDate) => (dueDate ? moment.utc(dueDate).format('YYYY-MM-DD') : null)

export const todayKey = (now = new Date()) => moment(now).tz(BUSINESS_TZ).format('YYYY-MM-DD')

// "YYYY-MM-DD" moved by whole days, e.g. yesterday = shiftDayKey(todayKey(), -1).
export const shiftDayKey = (key, days) => moment.utc(key).add(days, 'days').format('YYYY-MM-DD')

// Whole days from today to the due day: negative once it is late, 0 on the day, null if undated.
export const daysUntilDue = (dueDate, now = new Date()) => {
  const key = dueDateKey(dueDate)
  if (!key) return null
  return moment.utc(key).diff(moment.utc(todayKey(now)), 'days')
}

export const formatDueDate = (dueDate, format = 'DD/MM/YYYY') => (dueDate ? moment.utc(dueDate).format(format) : '')

/**
 * Where an open ticket stands against its due date: 'overdue', 'today', 'upcoming' or 'undated'.
 * Closed tickets are never late — a done ticket past its date is simply done.
 */
export const dueUrgency = (ticket, now = new Date()) => {
  const days = daysUntilDue(ticket.dueDate, now)
  if (days === null) return 'undated'
  if (!isOpenStatus(ticket.status)) return 'upcoming'
  if (days < 0) return 'overdue'
  if (days === 0) return 'today'
  return 'upcoming'
}

export const isOverdue = (ticket, now = new Date()) => dueUrgency(ticket, now) === 'overdue'

// Short Spanish phrase relative to today, e.g. "Venció hace 3 días" or "Vence mañana".
export const relativeDueText = (dueDate, now = new Date()) => {
  const days = daysUntilDue(dueDate, now)
  if (days === null) return 'Sin fecha límite'
  if (days < -1) return `Venció hace ${Math.abs(days)} días`
  if (days === -1) return 'Venció ayer'
  if (days === 0) return 'Vence hoy'
  if (days === 1) return 'Vence mañana'
  return `Vence en ${days} días`
}
